<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { not, required } from '@vuelidate/validators'
import { format as dateFormat } from 'date-fns'
import { capitalize } from 'lodash'
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useExtendedI18n } from '@/i18n'
import { VButton, VSection, VTextField, VLoading } from '@/modules/shared/components'
import { scrollToError } from '@/modules/shared/utils/form'
import { toNumber, initialMoney, times, minus, Money, sum, toMoney } from '@/modules/shared/utils/money'
import { allocateProRata } from '@/modules/shared/utils/allocate-pro-rata'
import { BatchTransaction } from '@/modules/investing/types'
import { Commitment, EntityExpanded } from '@/modules/investing/stores/better-entity-store'
import { useBatchTransactionStore } from '@/modules/investing/stores/batch-transaction-store'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { init } from '@sentry/vue'

type DisableReason = 'commitment_after_date' | 'no_capital_called' | null

type TransformedTransaction = {
  id: string | null
  is_active: boolean
  disable_reason: DisableReason
  capital: Money
  fee_percent: number | null
  commitment: Commitment
  ownership: number | null
  notes: string | null
  investor_allocating_id: string | null
}

///////////////////////////////////////////////////////////////////////////////
// Utils
///////////////////////////////////////////////////////////////////////////////

const initialTransactions = (commitments) =>
  commitments.map((commitment) => ({
    id: null,
    is_active: true,
    disable_reason: null as DisableReason,
    capital: initialMoney,
    fee_percent: null as number | null,
    commitment: { ...commitment },
    ownership:
      props.entity.ownership_type === 'assigned'
        ? commitment.ownership_override
        : commitment.ownership_by_commitment || 0,
  }))

const initializeBatchCall = () => {
  if (isNewRecord.value) {
    return {
      id: null,
      date: dateFormat(new Date(), 'yyyy-MM-dd'),
      selected_capital_type: '$',
      currency: props.entity.currency,
      entered_capital_amount: null,
      transaction_type: route.query.type || 'management_fee',
      transactions: initialTransactions(commitments.value),
    }
  }

  return {
    ...props.batchTransaction,
    transaction_type: props.batchTransaction.transactions[0].transaction_type,
    date: props.batchTransaction.date
      ? props.batchTransaction.date.split(/[T ]/)[0]
      : dateFormat(new Date(), 'yyyy-MM-dd'),
    investor_allocating: props.batchTransaction.investor_allocating_id,
    transactions: props.batchTransaction.transactions
      .map((transaction) => {
        const commitment = commitments.value.find((c) => c.id === transaction.commitment_id)

        return {
          id: transaction.id,
          is_active: true,
          disable_reason: null as DisableReason,
          capital: transaction.capital,
          fee_percent: null as number | null,
          commitment: { ...commitment },
          ownership:
            props.entity.ownership_type === 'assigned'
              ? commitment.ownership_override
              : commitment.ownership_by_commitment || 0,
        }
      })
      .sort((a, b) => a.commitment.shareholder.name.localeCompare(b.commitment.shareholder.name)),
  }
}

///////////////////////////////////////////////////////////////////////////////
// Setup
///////////////////////////////////////////////////////////////////////////////

const { n, t } = useExtendedI18n()
const { entity_id, entity_type, slug } = useRoute().params as { entity_id: string; entity_type: string; slug: string }
const route = useRoute()
const router = useRouter()
const entityStore = useEntityStore()
const commitments = computed(() => entityStore.listCommitments)

const batchTransactionStore = useBatchTransactionStore()

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const props = defineProps<{
  batchTransaction?: BatchTransaction
  commitments: Commitment[]
  entity: EntityExpanded
}>()

const emit = defineEmits<{
  (e: 'submit', payload: any): void
}>()

onMounted(() => {
  batchTransactionForm.value = initializeBatchCall()
  if (isPersisted.value) {
    macros.value.total_capital = props.batchTransaction.entered_capital_amount
    if (props.batchTransaction.selected_capital_type === '$') {
      macros.value.total_capital = toMoney(macros.value.total_capital, props.batchTransaction.currency)
    }
    selectedCapitalType.value = props.batchTransaction.selected_capital_type || '$'
    allocateManagementFees.value = props.batchTransaction.allocate_management_fees || '1'
    selectedTimePeriod.value = props.batchTransaction.selected_time_period || '1'
  } else {
    macroUpdateTransfer()
  }

  watch(
    () => allocateManagementFees.value,
    () => {
      macros.value.total_capital = null
      selectedTimePeriod.value = '1'
    },
  )

  watch(
    () => selectedCapitalType.value,
    () => {
      macros.value.total_capital = null
      batchTransactionForm.value.transactions.forEach((transaction) => {
        transaction.fee_percent = null
      })
      macroUpdateTransfer()
    },
  )

  watch(
    () => isPercentEditMode.value,
    (entering, wasEntering) => {
      if (!entering) return
      const isInitialMount = wasEntering === undefined
      if (isInitialMount && isPersisted.value) {
        const timePeriodMultiplier = getTimePeriodPercent(selectedTimePeriod.value)
        batchTransactionForm.value.transactions.forEach((transaction) => {
          if (transaction.fee_percent != null) return
          const denom = toNumber(times(transaction.commitment.committed, timePeriodMultiplier))
          transaction.fee_percent = denom > 0 ? (toNumber(transaction.capital) / denom) * 100 : 0
        })
        return
      }
      batchTransactionForm.value.transactions.forEach((transaction) => {
        if (transaction.fee_percent == null) {
          transaction.fee_percent = (getManagementFee(transaction) || 0) * 100
        }
        if (transaction.is_active) {
          transaction.capital = calculateCapital(transaction)
        }
      })
    },
    { immediate: true },
  )

  watch(
    () => selectedTimePeriod.value,
    () => {
      macroUpdateTransfer()
    },
  )

  watch(
    () => batchTransactionForm.value.transaction_type,
    () => {
      selectedCapitalType.value = '$'
      macros.value.total_capital = null
      macroUpdateTransfer()
    },
  )

  watch(
    () => batchTransactionForm.value.investor_allocating,
    () => {
      calculateOwnership()
    },
  )

  watch(
    [() => batchTransactionForm.value.transaction_type, () => batchTransactionForm.value.date],
    async ([newType, newDate], [oldType, oldDate]) => {
      if (newType === 'interest_allocation' && newDate !== oldDate) {
        loadingCommitmentTable.value = true
        await entityStore.fetchCommitments(entity_id, { slug, called_as_of: batchTransactionForm.value.date })
        batchTransactionForm.value.transactions = initialTransactions(commitments.value)
        calculateOwnership()
        loadingCommitmentTable.value = false
      } else if (newDate !== oldDate) {
        toggleAllInvestor(true)
      }
    },
  )
})

const isNewRecord = computed(() => !props.batchTransaction)
const isPersisted = computed(() => !isNewRecord.value)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const calculateOwnership = () => {
  batchTransactionForm.value.transactions = initialTransactions(commitments.value)
  const investorId = batchTransactionForm.value.investor_allocating?.investor_id
  const allocationDate = batchTransactionForm.value.date

  // Remove the paying investor from the list
  const nonPaying = batchTransactionForm.value.transactions.filter((t) => t.commitment.investor_id !== investorId)

  // Determine active status and disable reason
  nonPaying.forEach((t) => {
    const calledAmount = Number(t.commitment.called_as_of?.amount) || 0
    const commitmentDate = String(t.commitment.date).split(/[T ]/)[0]

    if (commitmentDate > allocationDate) {
      t.is_active = false
      t.disable_reason = 'commitment_after_date'
    } else if (calledAmount <= 0) {
      t.is_active = false
      t.disable_reason = 'no_capital_called'
    } else {
      t.is_active = true
      t.disable_reason = null
    }
  })

  // Compute ownership only among active investors
  computeActiveOwnership(nonPaying)
  batchTransactionForm.value.transactions = nonPaying
  macroUpdateTransfer()
}

const computeActiveOwnership = (transactions: TransformedTransaction[]) => {
  const active = transactions.filter((t) => t.is_active)
  const totalAmount = active.reduce((s, t) => s + (Number(t.commitment.called_as_of?.amount) || 0), 0)

  transactions.forEach((t) => {
    if (t.is_active && totalAmount > 0) {
      t.ownership = (Number(t.commitment.called_as_of.amount) || 0) / totalAmount
    } else {
      t.ownership = 0
      if (!t.is_active) t.capital = initialMoney
    }
  })
}

const recalculateActiveOwnership = () => {
  if (batchTransactionForm.value.transaction_type !== 'interest_allocation') return
  computeActiveOwnership(batchTransactionForm.value.transactions)
  macroUpdateTransfer()
}

const removeBatchTransaction = async () => {
  const confirm = window.confirm('Are you sure?')
  if (!confirm) return
  await batchTransactionStore.removeBatchTransaction(props.batchTransaction.id, { slug })
  router.push({
    name: 'investing.transactions',
    params: { entity_id: entity_id, entity_type: entity_type },
  })
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) {
    scrollToError(v$, Object.keys(rules))
    return
  }
  let entered_capital_amount = null
  if (selectedCapitalType.value === '$') {
    if (!!macros.value.total_capital) entered_capital_amount = toNumber(macros.value.total_capital)
  } else {
    entered_capital_amount = macros.value.total_capital
  }

  const payload = {
    id: batchTransactionForm.value.id,
    investor_set_id: entity_id,
    capital_amount: toNumber(totalCapital.value),
    currency: batchTransactionForm.value.currency,
    date: batchTransactionForm.value.date,
    entered_capital_amount: entered_capital_amount,
    selected_capital_type: selectedCapitalType.value,
    allocate_management_fees: null,
    selected_time_period: null,
    transactions_attributes: [],
    investor_allocating_id: null,
    investor_allocating_type: null,
    notes: null,
  }

  if (batchTransactionForm.value.transaction_type === 'management_fee') {
    payload.allocate_management_fees = allocateManagementFees.value
    payload.selected_time_period = selectedTimePeriod.value
  }

  if (batchTransactionForm.value.transaction_type === 'interest_allocation') {
    payload.investor_allocating_id = batchTransactionForm.value.investor_allocating?.investor_id
    payload.investor_allocating_type = batchTransactionForm.value.investor_allocating?.investor_type
    payload.notes = batchTransactionForm.value.notes
  }

  payload.transactions_attributes = batchTransactionForm.value.transactions
    .filter((obj) => {
      if (!obj.is_active) return false
      if (batchTransactionForm.value.transaction_type === 'interest_allocation') {
        if (batchTransactionForm.value.investor_allocating.investor_id === obj.commitment.shareholder_id) return false
      }
      return true
    })
    .map((transaction) => {
      return {
        id: transaction.id,
        investor_set_commitment_id: transaction.commitment.id,
        capital_amount: toNumber(transaction.capital || initialMoney),
        currency: batchTransactionForm.value.currency,
        transaction_type: batchTransactionForm.value.transaction_type,
      }
    })

  emit('submit', payload)
}

const transactionTypeLabels: Record<string, string> = {
  management_fee: 'Management Fee',
  other_fee: 'Other Fee',
  operating_income: 'Operating Income',
  operating_expense: 'Operating Expense',
  recycled_funds: 'Recycle Funds',
  syndication_fee: 'Syndication Fee',
  interest_allocation: 'Catch-up Interest',
}

const transactionTypeLabel = computed(
  () => transactionTypeLabels[batchTransactionForm.value?.transaction_type] || 'Transaction',
)

defineExpose({
  submit,
  transactionTypeLabel,
})

///////////////////////////////////////////////////////////////////////////////
// Calculations
///////////////////////////////////////////////////////////////////////////////

const getTimePeriodPercent = (time_period: string): number => {
  switch (time_period) {
    case '1':
      return 1
      break
    case '2':
      return 0.5
      break
    case '3':
      return 0.25
      break
    case '4':
      return 1 / 12
      break
    default:
      return 1
      break
  }
}

const calculateCapital = (transaction: TransformedTransaction) => {
  const ownership = transaction.ownership
  let total_capital = macros.value.total_capital || initialMoney

  if (batchTransactionForm.value.transaction_type === 'interest_allocation') {
    if (transaction.commitment.investor_id === batchTransactionForm.value.investor_allocating?.investor_id) {
      total_capital = minus(initialMoney, total_capital)
      return total_capital
    }
  }

  if (batchTransactionForm.value.transaction_type === 'management_fee' && allocateManagementFees.value === '1') {
    total_capital = times(transaction.commitment.committed, transaction.commitment.management_fee_percentage || 0)
  } else if (selectedCapitalType.value === '%') {
    const percent = transaction.fee_percent ?? (macros.value.total_capital as number) ?? 0
    total_capital = times(transaction.commitment.committed, percent / 100)
  } else {
    total_capital = times((macros.value.total_capital as Money) || initialMoney, ownership)
  }

  if (batchTransactionForm.value.transaction_type === 'management_fee') {
    const selectedTimePeriodPercent = getTimePeriodPercent(selectedTimePeriod.value)
    total_capital = times(total_capital, selectedTimePeriodPercent)
  }
  return total_capital
}

const getManagementFee = (transaction: TransformedTransaction) => {
  if (allocateManagementFees.value === '2' && macros.value.total_capital && selectedCapitalType.value === '%')
    return macros.value.total_capital / 100
  return transaction.commitment.management_fee_percentage || 0
}

const isPercentEditMode = computed(
  () =>
    batchTransactionForm.value?.transaction_type === 'management_fee' &&
    allocateManagementFees.value === '2' &&
    selectedCapitalType.value === '%',
)

const calculateFeePercent = (transaction: TransformedTransaction): number | null => {
  if (isPercentEditMode.value && macros.value.total_capital != null) {
    return macros.value.total_capital as number
  }
  return transaction.fee_percent
}

const totalCapital = computed(() =>
  batchTransactionForm.value.transactions.reduce((acc, transaction) => {
    if (transaction.is_active) {
      return sum([acc, transaction.capital])
    }
    return acc
  }, initialMoney),
)

const totalShowCapital = computed(() =>
  batchTransactionForm.value.transactions.reduce((acc, transaction) => {
    if (
      transaction.is_active &&
      transaction.commitment.investor_id !== batchTransactionForm.value.investor_allocating?.investor_id
    ) {
      return sum([acc, transaction.capital])
    }
    return acc
  }, initialMoney),
)

const clampPercent = (value: number | null): number | null => {
  if (value == null) return null
  if (value < 0) return 0
  if (value > 100) return 100
  return value
}

const onFeePercentChange = (transaction: TransformedTransaction) => {
  transaction.fee_percent = clampPercent(transaction.fee_percent)
  transaction.capital = calculateCapital(transaction)
}

const macroUpdateTransfer = () => {
  if (isPercentEditMode.value) {
    macros.value.total_capital = clampPercent(macros.value.total_capital as number | null)
  }
  const active = batchTransactionForm.value.transactions.filter((transaction) => transaction.is_active)
  active.forEach((transaction) => {
    transaction.fee_percent = calculateFeePercent(transaction)
    transaction.capital = calculateCapital(transaction)
  })

  // Apply LRM when a dollar total is split by ownership
  const isMgmtFeeDefault =
    batchTransactionForm.value.transaction_type === 'management_fee' && allocateManagementFees.value === '1'
  const isPercentMode = selectedCapitalType.value === '%'
  if (isMgmtFeeDefault || isPercentMode || !macros.value.total_capital) return

  let totalMoney = macros.value.total_capital as Money
  const isInterestAllocation = batchTransactionForm.value.transaction_type === 'interest_allocation'

  // For management_fee with custom %, the effective total is scaled by time period
  if (batchTransactionForm.value.transaction_type === 'management_fee') {
    totalMoney = times(totalMoney, getTimePeriodPercent(selectedTimePeriod.value))
  }

  // For interest_allocation, paying investor keeps -total; others split by ownership
  let lrmTargets = active
  if (isInterestAllocation) {
    const payingId = batchTransactionForm.value.investor_allocating?.investor_id
    lrmTargets = active.filter((t) => t.commitment.investor_id !== payingId)
  }

  const weights = lrmTargets.map((t) => t.ownership || 0)
  const allocations = allocateProRata(totalMoney, weights)
  lrmTargets.forEach((t, i) => {
    t.capital = allocations[i]
  })
}

const investorsAllocating = computed(() => {
  const pairs = props.commitments.map((transaction) => ({
    investor_id: transaction.investor_id,
    investor_name: transaction.shareholder.name,
    investor_type: transaction.shareholder_type,
  }))
  return pairs.filter((obj, index, self) => index === self.findIndex((o) => o.investor_id === obj.investor_id))
})

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////
const selectedCapitalType = ref('$')
const macros = ref({
  total_capital: null,
})
const rules = {
  transaction_type: { required },
  date: { required },
}
const batchTransactionForm: any = ref(null)
const v$ = useVuelidate(rules, batchTransactionForm, { $lazy: true })

const allocateManagementFees = ref('1') // 1: Use the default fee % as shown below, 2: Enter a fee to apply to all investors
const selectedTimePeriod = ref('1') // 1: Annually, 2: Semi-annually, 3: Quarterly, 4: Monthly

const loadingCommitmentTable = ref(false)

const isForceDisabled = (transaction: TransformedTransaction) =>
  !transaction.is_active && transaction.disable_reason !== null

const disableReasonText = (reason: DisableReason): string => {
  switch (reason) {
    case 'commitment_after_date':
      return 'Commitment started after transaction date'
    case 'no_capital_called':
      return 'No capital called as of transaction date'
    default:
      return ''
  }
}

const sortedTransactions = computed(() => {
  const txns = [...batchTransactionForm.value.transactions]
  return txns.sort((a, b) => {
    const aForceDisabled = !a.is_active && a.disable_reason !== null
    const bForceDisabled = !b.is_active && b.disable_reason !== null
    if (aForceDisabled !== bForceDisabled) return aForceDisabled ? 1 : -1
    return a.commitment.shareholder.name.localeCompare(b.commitment.shareholder.name)
  })
})

const isTransactionDisabled = (transaction: TransformedTransaction) => {
  if (!transaction.is_active) return true
  if (String(transaction.commitment.date).split(/[T ]/)[0] > batchTransactionForm.value.date) return true
  return false
}

///////////////////////////////////////////////////////////////////////////////
// Investor selection
///////////////////////////////////////////////////////////////////////////////

const toggleAllInvestor = (checked) => {
  if (isNewRecord.value) {
    batchTransactionForm.value.transactions.forEach((transaction) => {
      toggleInvestor({ target: { checked } }, transaction)
    })
    recalculateActiveOwnership()
  }
}

const toggleInvestor = (e, transaction) => {
  transaction.is_active = e.target.checked
  transaction.disable_reason = null

  const commitmentDate = String(transaction.commitment.date).split(/[T ]/)[0]
  if (commitmentDate > batchTransactionForm.value.date) {
    transaction.is_active = false
    transaction.disable_reason = 'commitment_after_date'
  } else if (batchTransactionForm.value.transaction_type === 'interest_allocation') {
    const calledAmount = Number(transaction.commitment.called_as_of?.amount) || 0
    if (calledAmount <= 0) {
      transaction.is_active = false
      transaction.disable_reason = 'no_capital_called'
    }
  }

  if (transaction.is_active) {
    transaction.capital = calculateCapital(transaction)
  } else {
    transaction.capital = initialMoney
  }
}

const onToggleInvestor = (e, transaction) => {
  toggleInvestor(e, transaction)
  recalculateActiveOwnership()
}
</script>

<template>
  <VSection class="mt-12 space-y-7 px-52" id="investor-allocations" v-if="batchTransactionForm">
    <div class="flex gap-3">
      <div class="w-1/2">
        <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
          {{ capitalize(t('shared.general information')) }}
          <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">1/2</span>
        </h3>
      </div>

      <div class="mt-6 w-1/2 space-y-7">
        <VTextField v-model="batchTransactionForm.date" label="Transaction Date" type="date" property="date" :v$="v$" />

        <div>
          <label for="x1" class="block text-sm font-medium leading-6 text-gray-900"> Type </label>
          <select
            id="x1"
            name="x1"
            v-model="batchTransactionForm.transaction_type"
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
            @change="macroUpdateTransfer"
          >
            <option :value="'management_fee'">Management Fee</option>
            <option :value="'other_fee'">Other Fee</option>
            <option :value="'operating_income'">Operating Income</option>
            <option :value="'operating_expense'">Operating Expense</option>
            <option :value="'recycled_funds'">Recycle Funds</option>
            <option :value="'syndication_fee'">Syndication Fee</option>
            <option :value="'interest_allocation'">Catch-up Interest</option>
          </select>
        </div>

        <div v-if="batchTransactionForm.transaction_type === 'management_fee'">
          <div class="mt-6">
            <label for="x2" class="block text-sm font-medium leading-6 text-gray-900">
              How would you like to allocate management fees?
            </label>
            <select
              id="x2"
              name="x2"
              v-model="allocateManagementFees"
              @change="macroUpdateTransfer"
              class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
            >
              <option value="1">Use the default fee % as shown below</option>
              <option value="2">Enter a fee to apply to all investors</option>
            </select>
          </div>
          <div class="mt-6" v-if="allocateManagementFees === '2'">
            <label for="x1" class="block text-sm font-medium leading-6 text-gray-900">
              Amount to allocate to investors in the grid below
            </label>
            <VTextField
              v-model="macros.total_capital"
              :type="selectedCapitalType === '$' ? 'currency' : 'percent'"
              :v$="v$"
              :useActionsSlot="true"
              inputClass="pl-16"
              :onInput="macroUpdateTransfer"
              :description="selectedCapitalType === '$' ? '' : `Percentage is being applied to total commitment`"
            >
              <template #actions>
                <div class="flex h-full py-1.5">
                  <kbd
                    class="inline-flex items-center rounded-l border border-gray-300 px-2 font-sans text-sm shadow-sm"
                    :class="[
                      selectedCapitalType === '$' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400',
                    ]"
                    @click="selectedCapitalType = '$'"
                    >$</kbd
                  >
                  <kbd
                    class="-ml-px inline-flex items-center rounded-r border border-gray-300 px-2 font-mono text-sm shadow-sm"
                    :class="[
                      selectedCapitalType === '%' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400',
                    ]"
                    @click="selectedCapitalType = '%'"
                    >%</kbd
                  >
                </div>
              </template>
            </VTextField>
          </div>
          <div class="mt-6" v-else>
            <label for="x1" class="block text-sm font-medium leading-6 text-gray-900">Select a time period </label>
            <div class="relative">
              <input
                id="x1"
                name="x1"
                type="text"
                class="mt-1 block w-full rounded-md border border-gray-300 pl-[340px] shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
              />
              <div class="absolute inset-y-0 left-0 flex py-1.5 pl-1.5">
                <kbd
                  class="inline-flex items-center rounded-l border border-gray-300 px-2 font-sans text-sm shadow-sm"
                  :class="[selectedTimePeriod === '1' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400']"
                  @click="selectedTimePeriod = '1'"
                >
                  Annually
                </kbd>
                <kbd
                  class="-ml-px inline-flex items-center border border-gray-300 px-2 font-sans text-sm shadow-sm"
                  :class="[selectedTimePeriod === '2' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400']"
                  @click="selectedTimePeriod = '2'"
                >
                  Semi-annually
                </kbd>
                <kbd
                  class="-ml-px inline-flex items-center border border-gray-300 px-2 font-sans text-sm shadow-sm"
                  :class="[selectedTimePeriod === '3' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400']"
                  @click="selectedTimePeriod = '3'"
                >
                  Quarterly
                </kbd>
                <kbd
                  class="-ml-px inline-flex items-center rounded-r border border-gray-300 px-2 font-sans text-sm shadow-sm"
                  :class="[selectedTimePeriod === '4' ? ' text-gray-900' : 'cursor-pointer bg-gray-200 text-gray-400']"
                  @click="selectedTimePeriod = '4'"
                >
                  Monthly
                </kbd>
              </div>
            </div>
          </div>
        </div>
        <div v-if="batchTransactionForm.transaction_type === 'interest_allocation'">
          <label for="investor_allocating" class="block text-sm font-medium leading-6 text-gray-900">
            Select the Investor paying the Interest
          </label>
          <select
            id="investor_allocating"
            name="investor_allocating"
            v-model="batchTransactionForm.investor_allocating"
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option v-for="transaction in investorsAllocating" :value="transaction">
              {{ transaction.investor_name }}
            </option>
          </select>
          <div class="mt-6">
            <label for="x1" class="block text-sm font-medium leading-6 text-gray-900">
              Enter the amount of interest paid
            </label>
          </div>
          <VTextField
            v-model="macros.total_capital"
            type="currency"
            :v$="v$"
            :onInput="macroUpdateTransfer"
            :description="'Interest amount is allocated to investors below based upon their percentage of capital called to date'"
          />
          <div class="mt-6">
            <label for="notes" class="block text-sm font-medium leading-6 text-gray-900"> Notes </label>
            <textarea
              id="notes"
              name="notes"
              rows="3"
              v-model="batchTransactionForm.notes"
              class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
            ></textarea>
          </div>
        </div>
        <div class="mt-6" v-else-if="batchTransactionForm.transaction_type !== 'management_fee'">
          <label for="x1" class="block text-sm font-medium leading-6 text-gray-900">
            Amount to allocate to investors in the grid below
          </label>
          <VTextField v-model="macros.total_capital" type="currency" :v$="v$" :onInput="macroUpdateTransfer" />
        </div>
      </div>
    </div>
  </VSection>
  <VSection class="space-y-7 px-52" id="investor-allocations" v-if="batchTransactionForm">
    <div class="flex gap-3">
      <div class="w-1/2">
        <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">
          {{ capitalize(t('shared.investor allocation')) }}
          <span class="ml-3 text-sm font-medium tracking-widest text-gray-400">2/2</span>
        </h3>
      </div>

      <div class="w-1/2 space-y-7"></div>
    </div>

    <div class="relative rounded-lg border border-gray-200">
      <template v-if="loadingCommitmentTable">
        <div class="absolute inset-0 mt-6 flex items-center justify-center">
          <VLoading />
        </div>
      </template>
      <table v-else class="rounded-lg">
        <thead class="stick">
          <tr>
            <th class="bg-white/50">
              <input
                class="inverse relative block h-4 w-4 rounded border-gray-300 text-white shadow-sm focus:ring-sky-300 focus:ring-opacity-50"
                type="checkbox"
                @change="toggleAllInvestor($event.target['checked'])"
                checked
                :disabled="isPersisted"
              />
            </th>
            <th class="bg-white/50 text-left">Investor</th>
            <th class="bg-white/50 text-right">
              <div class="flex items-center justify-end space-x-2">
                <div class="inline-block">Amount</div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(transaction, i) in sortedTransactions"
            v-show="transaction.commitment.investor_id !== batchTransactionForm.investor_allocating?.investor_id"
            :class="{ 'opacity-50': isForceDisabled(transaction) }"
          >
            <td
              class="px-3"
              :class="isForceDisabled(transaction) ? 'bg-gray-100' : !transaction.is_active ? 'bg-gray-50' : ''"
            >
              <input
                @change="(e) => onToggleInvestor(e, transaction)"
                type="checkbox"
                :checked="transaction.is_active"
                class="inverse relative block h-4 w-4 rounded border border-gray-300 text-white shadow-sm focus:ring-sky-300 focus:ring-opacity-50"
                :disabled="
                  isPersisted ||
                  isForceDisabled(transaction) ||
                  batchTransactionForm.investor_allocating?.investor_id === transaction.commitment.investor_id
                "
              />
            </td>
            <td
              class="border-r-[1px] border-gray-300 py-2 pl-3 text-base text-gray-700"
              :class="isForceDisabled(transaction) ? 'bg-gray-100 text-gray-400' : ''"
            >
              <div class="font-semibold">{{ transaction.commitment.shareholder.name }}</div>
              <div v-if="batchTransactionForm.transaction_type === 'interest_allocation'" class="flex gap-2 text-xs">
                <div class="">Total called:</div>
                <div class="">{{ n(transaction.commitment.called_as_of, 'currency') }}</div>
              </div>
              <div v-else class="flex gap-2 text-xs">
                <div class="">Commitment:</div>
                <div class="">{{ n(transaction.commitment.committed, 'currency') }}</div>
              </div>
              <div v-if="isForceDisabled(transaction)" class="mt-0.5 text-xs text-amber-600">
                {{ disableReasonText(transaction.disable_reason) }}
              </div>
            </td>
            <td :class="isForceDisabled(transaction) ? 'bg-gray-100' : ''">
              <div class="flex flex-row-reverse justify-between">
                <VTextField
                  v-model="transaction.capital"
                  :disabled="isTransactionDisabled(transaction) || isPercentEditMode"
                  :inputClass="`!border-none !shadow-none !mt-0 text-sm !rounded-none text-right ${
                    transaction.is_active ? 'bg-transparent' : ''
                  }`"
                  type="currency"
                />
                <VTextField
                  v-if="isPercentEditMode"
                  v-model="transaction.fee_percent"
                  :disabled="isTransactionDisabled(transaction)"
                  :onInput="() => onFeePercentChange(transaction)"
                  inputClass="!mt-0 !ml-3 !w-16 !py-0.5 !px-2 text-xs !rounded !border !border-sky-400 bg-sky-100 text-gray-900 hover:!bg-sky-200 focus:!bg-white"
                  type="percent"
                />
                <div
                  v-else-if="batchTransactionForm.transaction_type === 'management_fee'"
                  class="ml-5 h-fit w-fit rounded bg-sky-100 p-1 py-0.5 text-xs text-gray-500"
                >
                  {{ n(getManagementFee(transaction), 'percent') }}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td></td>
            <td>
              <VTextField
                v-model="totalShowCapital"
                :disabled="true"
                type="currency"
                inputClass="!border-none !shadow-none !mt-0 text-sm !rounded-none text-right text-gray-800 bg-transparent"
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </VSection>
  <VSection v-if="isPersisted" class="mt-24 space-y-8 px-52">
    <div class="h-1 bg-gray-100"></div>
    <h3 class="inline-flex items-center text-xl text-gray-900 lg:text-xl">Danger Zone</h3>
    <VButton :click="removeBatchTransaction" variant="v-red">Delete this call</VButton>
  </VSection>
</template>

<style scoped>
table {
  @apply w-full border-separate border-spacing-0 overflow-x-auto;
}

thead {
  @apply text-[12px] text-xs uppercase text-gray-700;
}

thead.stick th {
  @apply sticky top-[71px] z-50;
}

th {
  @apply min-w-[100px] whitespace-nowrap border-b border-r border-gray-200 px-3 py-3 backdrop-blur sm:min-w-[10px];
}

thead:first-child th:first-child {
  @apply rounded-tl-lg;
}

th:last-child {
  @apply rounded-tr-lg;
}

@media (min-width: 640px) {
  th.fixed-column {
    @apply first:sticky first:left-0 first:z-10 first:border-r;
  }

  td.fixed-column {
    @apply first:sticky first:left-0 first:z-[1] first:min-w-[320px] first:max-w-[480px] first:border-r first:bg-white;
  }

  tfoot td.fixed-column {
    @apply rounded-bl-lg;
  }
}

tbody {
  @apply text-sm text-gray-700;
}

tbody td {
  @apply whitespace-nowrap border-b border-r border-gray-200;
}

th:first-child,
td:first-child {
  @apply border-l-0;
}

th:last-child,
td:last-child {
  @apply border-r-0;
}

tfoot {
  @apply text-sm font-medium uppercase tracking-wide;
}

tfoot td {
  @apply whitespace-nowrap border-r border-gray-200 text-gray-900;
}

table tbody td,
table tfoot td {
  border-top: none !important;
}
</style>
